/**
 * SpendingExplorer — Phase 86.C. "Where your money went", everywhere.
 *
 * ONE component, ONE backing service (`GET /api/spending/breakdown`), used at
 * two scopes:
 *
 *   • Dashboard        — every eligible spending account
 *   • Account Analysis — a single account
 *
 * Previously the dashboard read `categories[]` off /api/dashboard/breakdown and
 * hand-rolled its own comparison, while CashTrendsTab ran a separate Supabase
 * query and grouped client-side on the raw amount with NO FX conversion. Two
 * implementations of one question, already disagreeing for multi-currency
 * users. Scope is a prop here; nothing else differs.
 *
 * Props
 *   accountIds   string[]  omit/empty for all accounts
 *   scopeLabel   string    e.g. "All spending accounts" | "HDFC Savings only"
 *   baseCurrency string
 *   compact      bool      dashboard density (fewer rows, no donut)
 *   defaultPeriod string
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { ArrowRight, Calendar as CalendarIcon, Info } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { formatMoney } from '@/lib/format';
import { colorForCategory } from '@/lib/categorize';
import { fetchSpendingBreakdown, SPENDING_PERIODS } from '@/lib/spendingApi';

function fmtRange(period) {
  if (!period) return '';
  try {
    const s = new Date(period.start);
    const e = new Date(period.end);
    const opts = { day: 'numeric', month: 'short' };
    const sameYear = s.getFullYear() === e.getFullYear();
    return `${s.toLocaleDateString(undefined, opts)} – ${e.toLocaleDateString(
      undefined, sameYear ? opts : { ...opts, year: 'numeric' })}`;
  } catch { return `${period.start} – ${period.end}`; }
}

/** Stable colour per group. Categories use the shared palette so a slice is the
 *  same colour here, on the dashboard and on the account page. */
function colorFor(group, grouping, index) {
  if (grouping === 'category') return colorForCategory(group.label);
  return colorForCategory(`${group.key}-${index}`);
}

function DeltaBadge({ delta_pct }) {
  if (delta_pct === null || delta_pct === undefined) return null;
  const up = delta_pct > 0;
  if (Math.abs(delta_pct) < 1) {
    return <span className="text-[11px] text-muted-foreground/70 tabular-nums">flat</span>;
  }
  return (
    <span
      className="text-[11px] tabular-nums font-medium"
      style={{ color: up ? '#A84E38' : '#3D7A63' }}
      title="Versus the equivalent previous period"
    >
      {up ? '↑' : '↓'} {Math.abs(Math.round(delta_pct))}%
    </span>
  );
}

function SliceTooltip({ active, payload, baseCurrency }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 shadow-sm">
      <p className="text-[11px] text-muted-foreground mb-0.5">{d.label}</p>
      <p className="num text-sm font-semibold text-popover-foreground">
        {formatMoney(d.amount, baseCurrency)}
      </p>
      <p className="text-[10px] text-muted-foreground">{d.share}% · {d.txn_count} txns</p>
    </div>
  );
}

export default function SpendingExplorer({
  accountIds = [],
  scopeLabel,
  baseCurrency: baseCurrencyProp = 'INR',
  compact = false,
  defaultPeriod = 'this_month',
  testidPrefix = 'spending-explorer',
}) {
  const [period, setPeriod]     = useState(defaultPeriod);
  const [grouping, setGrouping] = useState('category');
  const [custom, setCustom]     = useState({ start: '', end: '' });
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  // Monotonic request id: the user can switch period or grouping faster than the
  // service responds, and a late-arriving earlier response must never overwrite
  // the newer one it lost the race to.
  const reqIdRef = useRef(0);

  const idsKey = accountIds.join(',');
  const awaitingDates = period === 'custom' && (!custom.start || !custom.end);

  const load = useCallback(async () => {
    // Custom with no dates yet isn't a request — and must not leave the
    // component stuck in its loading skeleton with nothing to show.
    if (awaitingDates) { setLoading(false); return; }
    const myReq = ++reqIdRef.current;
    setLoading(true);
    const res = await fetchSpendingBreakdown({
      accountIds, period, grouping,
      start: custom.start, end: custom.end,
    });
    if (myReq !== reqIdRef.current) return;   // superseded; drop it
    setData(res);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, period, grouping, custom.start, custom.end, awaitingDates]);

  useEffect(() => { load(); }, [load]);

  // Render money in the currency the DATA says it is in. Two reasons:
  //   1. The caller's prop can still be the 'INR' placeholder while the currency
  //      context resolves, which would label genuine AED totals with a ₹ sign.
  //      A number under the wrong symbol is worse than no number.
  //   2. A single-currency scope is reported NATIVELY by the service (an account
  //      is shown in its own currency), so this is often not the base currency.
  const baseCurrency = data?.display_currency || data?.base_currency || baseCurrencyProp;

  const groups = data?.groups || [];
  const shown = compact ? groups.slice(0, 5) : groups;
  const total = data?.total_spent || 0;
  const cmp = data?.comparison;

  const totalDelta = useMemo(() => {
    if (!cmp?.total || !total) return null;
    return Math.round(((total - cmp.total) / cmp.total) * 100);
  }, [cmp, total]);

  /** Drill-through preserves the exact scope AND window the user is looking at,
   *  so the evidence page can never show a different set of transactions than
   *  the number they clicked. */
  const drillTo = (group) => {
    const p = new URLSearchParams({ type: 'expense' });
    if (data?.period) { p.set('from', data.period.start); p.set('to', data.period.end); }
    if (group) {
      if (grouping === 'category' && !group.is_aggregate) p.set('category', group.label);
      if (grouping === 'merchant' && !group.is_aggregate) p.set('q', group.label);
    }
    if (accountIds.length === 1) p.set('account', accountIds[0]);
    return `/transactions?${p.toString()}`;
  };

  return (
    <section
      className="bg-card rounded-xl border border-border shadow-sm overflow-hidden"
      data-testid={testidPrefix}
    >
      <header className="px-5 pt-5 pb-4 border-b border-border/60">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-charcoal">Where your money went</h3>
            <p className="text-xs text-muted-foreground mt-0.5" data-testid={`${testidPrefix}-scope`}>
              {scopeLabel || (accountIds.length ? 'This account only' : 'All spending accounts')}
              {data?.period && !awaitingDates && <> · {fmtRange(data.period)}</>}
            </p>
          </div>
          {/* Category / Merchant */}
          <div className="flex rounded-full bg-muted/50 p-0.5" data-testid={`${testidPrefix}-grouping`}>
            {['category', 'merchant'].map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGrouping(g)}
                className={`rounded-full px-3 py-1 text-xs font-medium capitalize transition-colors ${
                  grouping === g
                    ? 'bg-card text-charcoal shadow-sm'
                    : 'text-muted-foreground hover:text-charcoal'}`}
                data-testid={`${testidPrefix}-group-${g}`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>

        {/* Period chips */}
        <div className="flex items-center gap-1.5 flex-wrap mt-3">
          <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground mr-0.5" />
          {SPENDING_PERIODS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => setPeriod(p.value)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                period === p.value
                  ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]'
                  : 'bg-muted/40 text-muted-foreground hover:bg-muted/70'}`}
              data-testid={`${testidPrefix}-period-${p.value}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {period === 'custom' && (
          <div className="flex items-center gap-2 mt-3" data-testid={`${testidPrefix}-custom-range`}>
            <input
              type="date" value={custom.start}
              onChange={(e) => setCustom((c) => ({ ...c, start: e.target.value }))}
              className="rounded-lg border border-border bg-background px-2.5 py-1.5 text-xs"
              data-testid={`${testidPrefix}-custom-start`}
            />
            <span className="text-xs text-muted-foreground">to</span>
            <input
              type="date" value={custom.end}
              onChange={(e) => setCustom((c) => ({ ...c, end: e.target.value }))}
              className="rounded-lg border border-border bg-background px-2.5 py-1.5 text-xs"
              data-testid={`${testidPrefix}-custom-end`}
            />
          </div>
        )}
      </header>

      {awaitingDates ? (
        <p className="p-5 text-xs text-muted-foreground" data-testid={`${testidPrefix}-awaiting-dates`}>
          Choose a start and end date to see this range.
        </p>
      ) : loading ? (
        <div className="p-5 space-y-3">
          <Skeleton className="h-10 w-48" />
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-8 w-full" />)}
        </div>
      ) : !data ? (
        <div className="p-5" data-testid={`${testidPrefix}-error`}>
          <p className="text-xs text-muted-foreground">
            We couldn&rsquo;t load your spending just now. Your data is safe.
          </p>
          <button
            type="button"
            onClick={load}
            className="mt-2 text-xs font-medium text-primary hover:underline"
            data-testid={`${testidPrefix}-retry`}
          >
            Try again
          </button>
        </div>
      ) : (
        <div className="p-5">
          {/* Total + comparison */}
          <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-1">
                Total spent
              </p>
              <p className="num font-display text-2xl font-medium text-charcoal"
                 data-testid={`${testidPrefix}-total`}>
                {formatMoney(total, baseCurrency)}
              </p>
              <p className="text-[11px] text-muted-foreground mt-1">
                {data.txn_count} transaction{data.txn_count === 1 ? '' : 's'}
                {data.converted && (
                  <> · converted to {data.display_currency}</>
                )}
              </p>
            </div>
            {cmp && (
              <div className="text-right" data-testid={`${testidPrefix}-comparison`}>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-1">
                  vs {cmp.label}
                </p>
                <p className="num text-sm text-charcoal">{formatMoney(cmp.total || 0, baseCurrency)}</p>
                {totalDelta !== null && (
                  <p className="text-[11px] font-medium mt-0.5"
                     style={{ color: totalDelta > 0 ? '#A84E38' : '#3D7A63' }}>
                    {totalDelta > 0 ? '↑' : '↓'} {Math.abs(totalDelta)}%
                  </p>
                )}
              </div>
            )}
          </div>

          {groups.length === 0 ? (
            <p className="text-xs text-muted-foreground py-6 text-center" data-testid={`${testidPrefix}-empty`}>
              No spending recorded in this period
              {accountIds.length === 1 ? ' for this account' : ''}.
            </p>
          ) : (
            <div className={compact ? '' : 'grid lg:grid-cols-5 gap-6'}>
              {!compact && (
                <div className="lg:col-span-2">
                  <div className="h-56" data-testid={`${testidPrefix}-donut`}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={groups} dataKey="amount" nameKey="label"
                          innerRadius={48} outerRadius={84} paddingAngle={2}
                          stroke="hsl(var(--background))" strokeWidth={1}
                        >
                          {groups.map((g, i) => (
                            <Cell key={g.key} fill={colorFor(g, grouping, i)} />
                          ))}
                        </Pie>
                        <Tooltip content={<SliceTooltip baseCurrency={baseCurrency} />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              <div className={compact ? '' : 'lg:col-span-3'}>
                <ul className="divide-y divide-border/40">
                  {shown.map((g, i) => (
                    <li key={g.key} data-testid={`${testidPrefix}-row-${g.key}`}>
                      <Link
                        to={drillTo(g)}
                        className="flex items-center gap-3 py-2.5 group hover:bg-offwhite/60 -mx-2 px-2 rounded-lg transition-colors"
                      >
                        <span className="w-2 h-2 rounded-full shrink-0"
                              style={{ backgroundColor: colorFor(g, grouping, i) }} />
                        <span className="text-sm text-charcoal truncate flex-1">{g.label}</span>
                        <DeltaBadge delta_pct={g.delta_pct} />
                        <span className="num text-xs text-muted-foreground w-12 text-right tabular-nums">
                          {g.share}%
                        </span>
                        <span className="num text-sm font-medium text-charcoal w-24 text-right tabular-nums">
                          {formatMoney(g.amount, baseCurrency)}
                        </span>
                        <ArrowRight className="h-3 w-3 text-muted-foreground/0 group-hover:text-muted-foreground/70 transition-colors shrink-0" />
                      </Link>
                    </li>
                  ))}
                </ul>
                {compact && groups.length > shown.length && (
                  <Link to={drillTo(null)}
                        className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline mt-3"
                        data-testid={`${testidPrefix}-see-all`}>
                    See all {groups.length} <ArrowRight className="h-3 w-3" />
                  </Link>
                )}
              </div>
            </div>
          )}

          {/* One observation, not a wall of them */}
          {data.observation && (
            <p className="text-xs text-charcoal mt-5 pt-4 border-t border-border/60 flex items-start gap-2"
               data-testid={`${testidPrefix}-observation`}>
              <Info className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
              {data.observation.text}
            </p>
          )}

          {/* Transfers and debt repayments are not spending — say so rather than
              quietly dropping them. */}
          {(data.excluded?.transfers > 0 || data.excluded?.debt_payments > 0
            || data.excluded?.investments > 0) && (
            <p className="text-[11px] text-muted-foreground mt-3" data-testid={`${testidPrefix}-excluded-note`}>
              Excludes{' '}
              {[
                data.excluded.debt_payments > 0
                  && `${formatMoney(data.excluded.debt_payments, baseCurrency)} of loan and card repayments`,
                data.excluded.investments > 0
                  && `${formatMoney(data.excluded.investments, baseCurrency)} invested`,
                data.excluded.transfers > 0
                  && `${formatMoney(data.excluded.transfers, baseCurrency)} moved between your own accounts`,
              ].filter(Boolean).reduce((acc, part, i, arr) => (
                i === 0 ? [part] : [...acc, i === arr.length - 1 ? ` and ${part}` : `, ${part}`]
              ), []).join('')}
              {' '}&mdash; money moved or invested, not money spent.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
